import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { cx } from "../cx.ts";
import { IconButton } from "./IconButton.tsx";

export type SheetSide = "bottom" | "right" | "centre";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Where the sheet sits: bottom (phones), right (desktop side panel) or centre (dialog). */
  side?: SheetSide;
  /** Sticky action row (Save, Cancel). */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

const SIDES: Record<SheetSide, string> = {
  bottom:
    "mt-auto mb-0 w-full max-w-none rounded-t-2xl max-h-[92dvh] sm:mb-auto sm:max-w-lg sm:rounded-2xl",
  right: "mr-0 ml-auto h-dvh max-h-none w-full max-w-md rounded-none sm:rounded-l-2xl",
  centre: "w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[90dvh]",
};

/**
 * A modal sheet on the native <dialog> element: the browser traps focus,
 * closes on Escape, restores focus on close and makes the page inert, so
 * no portal or focus library is needed. A tap on the backdrop closes it.
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  side = "bottom",
  footer,
  className,
  children,
}: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the backdrop click is a mouse shortcut; Escape (onCancel) and the Close button are the keyboard paths.
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      className={cx(
        "sq-sheet flex-col overflow-hidden border border-line bg-surface p-0 text-ink shadow-2xl open:flex",
        "sun:border-2 sun:border-black",
        SIDES[side],
        className,
      )}
      onCancel={(e) => {
        // Escape: let React state decide, so a parent can veto (unsaved changes).
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // A click on the dialog element itself (not its contents) is a click on the backdrop.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <header className="flex items-start gap-3 border-b border-line px-4 py-3 sun:border-black">
        <div className="min-w-0 flex-1 pt-2">
          <h2 id={titleId} className="font-semibold text-lg leading-6">
            {title}
          </h2>
          {description ? (
            <p id={descId} className="mt-1 text-ink-muted text-sm sun:text-min">
              {description}
            </p>
          ) : null}
        </div>
        <IconButton label="Close" icon={<X aria-hidden className="size-5" />} onClick={onClose} />
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">{children}</div>
      {footer ? (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sun:border-black">
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}

/** A sheet that rises from the bottom of the screen (centred on wider screens). */
export function BottomSheet(props: Omit<SheetProps, "side">) {
  return <Sheet {...props} side="bottom" />;
}
